import {
    CustomerBusinessStatus as CustomerBusinessStatusMap,
    SrCustomerStatus,
    SrInsightType,
} from '@plato/data/starrocks/types';
import { z } from 'zod';
import { richTextSchema } from '../types/common-types.ts';
import { customerAddressDtoSchema } from './customer-address-dto.ts';
import { salesRepDtoSchema } from './sales-rep-dto.ts';

export const CustomerBusinessStatus = z.enum(CustomerBusinessStatusMap);

export const customerDtoSchema = z.object({
    address: customerAddressDtoSchema,
    customerClass: z.string().optional(),
    customerGroupId: z.string().optional(),
    customerId: z.string(),
    customerName: z.string().optional(),
    industrySector: z.string().nullable().optional(),
    salesReps: z.array(salesRepDtoSchema),
    platoStatus: z.enum(SrCustomerStatus),
});

export type CustomerDTO = z.infer<typeof customerDtoSchema>;

export const customerFilterSchema = z.object({
    salesRepIds: z.array(z.string()).optional().describe('Sales rep IDs.'),
    cityNames: z.array(z.string()).optional().describe('Customer city names (exact).'),
    zipCodes: z.array(z.string()).optional().describe('Customer zip codes (exact).'),
    industryNames: z.array(z.string()).optional().describe('Industry sector names.'),
    insightTypes: z
        .array(z.enum(SrInsightType))
        .optional()
        .describe('Only customers with an open insight of these types.'),
    statuses: z.array(z.string()).optional().describe('Customer statuses.'),
    searchString: z.string().optional().describe('Customer name substring match.'),
    companyIds: z.array(z.string()).optional().describe('Company IDs.'),
    customerClasses: z.array(z.string()).optional().describe('Customer classes.'),
    salesRegions: z.array(z.string()).optional().describe('Sales regions.'),
    customerIds: z.array(z.string()).optional().describe('Specific ERP customer IDs.'),
    businessStatus: CustomerBusinessStatus.optional().describe('CRM business status.'),
    excludeErpStatusBlocked: z
        .boolean()
        .optional()
        .describe('Drop customers the ERP exports as blocked (customer_dim.status = BLOCKED).'),
});

export type CustomerFilterOptions = z.infer<typeof customerFilterSchema>;

export const crmCustomerUpdateBusinessStatusInputSchema = z.object({
    customerId: z.string(),
    businessStatus: CustomerBusinessStatus,
    reason: richTextSchema.nullish(),
});

export const crmCustomerUpdateBusinessStatusOutputSchema = z.object({
    customerId: z.string(),
    fromStatus: CustomerBusinessStatus.optional(),
    toStatus: CustomerBusinessStatus,
    reason: richTextSchema.nullish(),
});

export const crmCustomerVisitPreferencesUpdateInputSchema = z.object({
    customerId: z.string(),
    isVisited: z.boolean().optional(),
    // `null` (rather than `undefined`) is required here: undo has to clear a cadence the user just
    // set, and `undefined` means "leave this column alone" all the way down to Prisma.
    visitCadenceInWeeks: z.number().int().positive().nullish(),
});

export type CrmCustomerBusinessStatusUpdateInput = z.output<
    typeof crmCustomerUpdateBusinessStatusInputSchema
>;
export type CrmCustomerBusinessStatusUpdateOutput = z.output<
    typeof crmCustomerUpdateBusinessStatusOutputSchema
>;

export type CrmCustomerVisitPreferencesUpdateInput = z.output<
    typeof crmCustomerVisitPreferencesUpdateInputSchema
>;
